import { base64ToUint8Array } from './media-source-compat';

/**
 * Keeps the TTS SourceBuffer from ever running dry between utterances.
 *
 * Read-along appends one utterance and then nothing until the next sentence
 * arrives, so the buffer drains to zero in every gap. That underrun is what
 * eats the first word:
 *
 *   1. The element fires `waiting` and the renderer stops.
 *   2. kickPlayback recovers with a seek plus play(), so playback restarts
 *      from a stopped renderer rather than continuing.
 *   3. WebKit does not compensate the restart for the route's output latency,
 *      so the playhead lands *past* where audio actually resumes. Measured on
 *      a zero-latency sink the loss is ~45 ms, on built-in speakers it is
 *      inaudible, and on Bluetooth (A2DP output latency 150-250 ms) it is a
 *      whole short word: "hello" is heard as "o".
 *   4. A drained A2DP link also lets the accessory idle, and waking it costs
 *      a further 130-270 ms of the head.
 *
 * Feeding a small cushion of silence removes the underrun, so none of that
 * chain fires. Measured in Safari/WebKit over three utterances: baseline
 * gives 4 `waiting` events and a buffer that hits exactly 0 in every gap; a
 * 150 ms cushion gives 1 (startup only), zero recovery seeks, and one
 * unbroken `played` range.
 *
 * Two constraints learned by measurement rather than from the spec:
 *
 *   - WebKit silently ignores an append of byte-identical data twice in a
 *     row. `timestampOffset` does not advance and the buffer does not grow,
 *     while Blink extends normally. So the fragments must be rotated, never
 *     repeated. This is why FRAGMENTS holds four distinct fragments.
 *   - WebKit fires `waiting` with as much as 30 ms still buffered ahead, so
 *     the cushion has to clear that by a comfortable margin.
 */

// Target amount of audio to keep buffered ahead of the playhead. 150 ms is
// the measured minimum that keeps WebKit out of the stalled state, but the
// cushion also has to survive a backgrounded page, where the only reliable
// refill trigger is `timeupdate` at roughly 250 ms. 400 ms clears that with
// margin. It is also the worst-case delay this adds to a new utterance, which
// is why it is not set higher still.
export const CUSHION_MS = 400;

// Four AAC-LC silence fragments, byte-compatible with the init segment every
// provider emits (speech-processor wraps all of them through the same ffmpeg
// settings: AAC-LC, 48 kHz, mono). They differ only in mfhd sequence number
// and tfdt decode time; in `sequence` mode the SourceBuffer overrides those
// with its own timestampOffset, so rotating them yields continuous silence.
// 132 bytes each, 5 AAC frames of 1024 samples = 106.7 ms per fragment.
const FRAGMENTS_B64 = [
  'AAAAaG1vb2YAAAAQbWZoZAAAAAAAAAACAAAAUHRyYWYAAAAgdGZoZAACADoAAAABAAAAAQAABAAAAAAEAgAAAAAAABR0ZmR0AQAAAAAAAAAAABQAAAAAFHRydW4BAAABAAAABQAAAHAAAAAcbWRhdAEYIAcBGCAHARggBwEYIAcBGCAH',
  'AAAAaG1vb2YAAAAQbWZoZAAAAAAAAAADAAAAUHRyYWYAAAAgdGZoZAACADoAAAABAAAAAQAABAAAAAAEAgAAAAAAABR0ZmR0AQAAAAAAAAAAACgAAAAAFHRydW4BAAABAAAABQAAAHAAAAAcbWRhdAEYIAcBGCAHARggBwEYIAcBGCAH',
  'AAAAaG1vb2YAAAAQbWZoZAAAAAAAAAAEAAAAUHRyYWYAAAAgdGZoZAACADoAAAABAAAAAQAABAAAAAAEAgAAAAAAABR0ZmR0AQAAAAAAAAAAADwAAAAAFHRydW4BAAABAAAABQAAAHAAAAAcbWRhdAEYIAcBGCAHARggBwEYIAcBGCAH',
  'AAAAaG1vb2YAAAAQbWZoZAAAAAAAAAAFAAAAUHRyYWYAAAAgdGZoZAACADoAAAABAAAAAQAABAAAAAAEAgAAAAAAABR0ZmR0AQAAAAAAAAAAAFAAAAAAFHRydW4BAAABAAAABQAAAHAAAAAcbWRhdAEYIAcBGCAHARggBwEYIAcBGCAH',
];

export const FRAGMENT_MS = (5 * 1024 * 1000) / 48000;

// If the buffer fails to grow this many times in a row the fragments are not
// compatible with whatever init segment the server sent. Stand down rather
// than append forever into a SourceBuffer that ignores us, so a codec change
// downgrades to the old behaviour instead of wedging playback.
const MAX_NOOP_APPENDS = 3;

export class TtsSilenceFiller {
  private fragments: Uint8Array[] | null = null;
  private index = 0;
  private noops = 0;
  private standDown = false;

  /** False once the fragments have proven incompatible with the stream. */
  get enabled(): boolean {
    return !this.standDown;
  }

  /**
   * The next fragment to append, or null if the filler has stood down.
   * Rotates so the same bytes are never appended twice in a row, which WebKit
   * would ignore.
   */
  next(): Uint8Array | null {
    if (this.standDown) {
      return null;
    }
    if (!this.fragments) {
      this.fragments = FRAGMENTS_B64.map(base64ToUint8Array);
    }
    const frag = this.fragments[this.index % this.fragments.length];
    this.index += 1;
    return frag;
  }

  /**
   * Report whether the append actually extended the buffer. Repeated no-ops
   * mean the fragments do not match the stream, so the filler stands down.
   */
  noteAppendGrew(grew: boolean): void {
    if (grew) {
      this.noops = 0;
      return;
    }
    this.noops += 1;
    if (this.noops >= MAX_NOOP_APPENDS) {
      this.standDown = true;
      console.warn(
        `[TTS] silence filler stood down after ${this.noops} appends that did not extend the buffer`,
      );
    }
  }

  /** True if the chunk is one of ours, so the caller can treat it as filler. */
  isFiller(chunk: Uint8Array): boolean {
    return this.fragments !== null && this.fragments.includes(chunk);
  }

  /** Fresh stream, so give the fragments another chance. */
  reset(): void {
    this.index = 0;
    this.noops = 0;
    this.standDown = false;
  }
}
